import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Search, FileText, Mail, ClipboardList, ClipboardCheck, ScrollText, Clock } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import EditParcelaModal from '../../components/Admin/EditParcelaModal';
import ClienteDocumentosSection from '../../components/Admin/ClienteDocumentosSection';
import { Moeda, formatMoeda } from '../../utils/orcamentoPagamento';
import { ActionButton, Button, IconButton } from '../../components/UI';

interface Cliente {
  _id: string;
  nome: string;
  email?: string;
  telefone?: string;
  passaporteNumero?: string;
  nif?: string;
  cpf?: string;
  endereco?: { cidade?: string; pais?: string };
  status: 'lead' | 'negociacao' | 'cliente_ativo' | 'inativo';
  origem?: string;
  observacoes?: string;
  responsavelId?: { name: string };
}

interface RegistrosVinculados {
  contacts: any[];
  nifs: any[];
  visaAnalyses: any[];
  residenceAnalyses: any[];
  contratos: any[];
}

interface Venda {
  _id: string;
  descricao: string;
  valorTotal: number;
  moeda?: Moeda;
  formaPagamento: string;
  status: string;
  dataVenda: string;
}

interface Pagamento {
  _id: string;
  numeroParcela: number;
  totalParcelas: number;
  valor: number;
  moeda?: Moeda;
  vencimento: string;
  status: 'pendente' | 'pago' | 'atrasado' | 'cancelado';
}

interface HistoricoItem {
  _id: string;
  tipo: string;
  descricao: string;
  autor?: { name: string };
  automatico: boolean;
  createdAt: string;
}

const STATUS_LABELS: { [key: string]: string } = {
  lead: 'Lead',
  negociacao: 'Em negociação',
  cliente_ativo: 'Cliente ativo',
  inativo: 'Inativo'
};

const STATUS_COLORS: { [key: string]: string } = {
  lead: 'bg-gray-100 text-gray-800',
  negociacao: 'bg-blue-100 text-blue-800',
  cliente_ativo: 'bg-green-100 text-green-800',
  inativo: 'bg-red-100 text-red-800'
};

const PAGAMENTO_STATUS_COLORS: { [key: string]: string } = {
  pendente: 'bg-yellow-100 text-yellow-800',
  pago: 'bg-green-100 text-green-800',
  atrasado: 'bg-red-100 text-red-800',
  cancelado: 'bg-gray-100 text-gray-800'
};

const LINK_TYPES: { tipo: string; label: string; icon: React.ElementType }[] = [
  { tipo: 'contact', label: 'Contatos', icon: Mail },
  { tipo: 'nif', label: 'NIF', icon: FileText },
  { tipo: 'visaAnalysis', label: 'Análises de visto', icon: ClipboardList },
  { tipo: 'residenceAnalysis', label: 'Análises de residência', icon: ClipboardCheck },
  { tipo: 'contrato', label: 'Contratos', icon: ScrollText }
];

const ClienteDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirm = useConfirm();

  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [registrosVinculados, setRegistrosVinculados] = useState<RegistrosVinculados>({
    contacts: [], nifs: [], visaAnalyses: [], residenceAnalyses: [], contratos: []
  });
  const [vendas, setVendas] = useState<Venda[]>([]);
  const [pagamentos, setPagamentos] = useState<Pagamento[]>([]);
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [novaNota, setNovaNota] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [linkingTipo, setLinkingTipo] = useState<string | null>(null);
  const [linkQuery, setLinkQuery] = useState('');
  const [linkResults, setLinkResults] = useState<any[]>([]);
  const [linking, setLinking] = useState(false);
  const [editingPagamento, setEditingPagamento] = useState<Pagamento | null>(null);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/clientes/${id}`);
      setCliente(response.data.cliente);
      setRegistrosVinculados(response.data.registrosVinculados);
      setVendas(response.data.vendas);
      setPagamentos(response.data.pagamentos);
      setHistorico(response.data.historico);
    } catch (error) {
      console.error('Erro ao carregar ficha do cliente:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Atualiza só o histórico (sem o spinner de página inteira do fetchDetail)
  const refreshHistorico = useCallback(async () => {
    try {
      const response = await axios.get(`/clientes/${id}/historico`, { params: { limit: 100 } });
      setHistorico(response.data.historico);
    } catch (error) {
      console.error('Erro ao atualizar histórico do cliente:', error);
    }
  }, [id]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('pt-BR', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('pt-BR', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const handleAddNote = async () => {
    if (!novaNota.trim()) return;
    try {
      setAddingNote(true);
      const response = await axios.post(`/clientes/${id}/historico`, { descricao: novaNota });
      setHistorico((prev) => [response.data, ...prev]);
      setNovaNota('');
    } catch (error: any) {
      console.error('Erro ao adicionar nota:', error);
      notifications.error.clienteUpdate(error.response?.data?.message || 'Erro ao adicionar nota');
    } finally {
      setAddingNote(false);
    }
  };

  const handleMarcarPago = async (pagamentoId: string) => {
    try {
      const response = await axios.put(`/pagamentos/${pagamentoId}/pagar`);
      setPagamentos((prev) => prev.map((p) => (p._id === pagamentoId ? response.data : p)));
      notifications.success.pagamentoRegistrado();
      fetchDetail();
    } catch (error: any) {
      console.error('Erro ao marcar pagamento como pago:', error);
      notifications.error.pagamentoAction(error.response?.data?.message || 'Erro ao marcar pagamento como pago');
    }
  };

  const handleExcluirPagamento = async (pagamentoId: string) => {
    if (!(await confirm({ title: 'Excluir parcela', message: 'Esta parcela será removida permanentemente.', confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/pagamentos/${pagamentoId}`);
      setPagamentos((prev) => prev.filter((p) => p._id !== pagamentoId));
      notifications.success.pagamentoExcluido();
    } catch (error: any) {
      console.error('Erro ao excluir pagamento:', error);
      notifications.error.pagamentoAction(error.response?.data?.message || 'Erro ao excluir parcela');
    }
  };

  const handleSearchLink = useCallback(async () => {
    if (!linkingTipo) return;
    try {
      const response = await axios.get('/clientes/search-unlinked', {
        params: { tipo: linkingTipo, q: linkQuery }
      });
      setLinkResults(response.data.results);
    } catch (error) {
      console.error('Erro ao buscar registros não vinculados:', error);
    }
  }, [linkingTipo, linkQuery]);

  useEffect(() => {
    if (linkingTipo) {
      handleSearchLink();
    }
  }, [linkingTipo, handleSearchLink]);

  const handleLinkRecord = async (registroId: string) => {
    if (!linkingTipo) return;
    try {
      setLinking(true);
      await axios.patch(`/clientes/${id}/link`, { tipo: linkingTipo, registroId });
      setLinkingTipo(null);
      setLinkQuery('');
      setLinkResults([]);
      fetchDetail();
    } catch (error: any) {
      console.error('Erro ao vincular registro:', error);
      notifications.error.clienteUpdate(error.response?.data?.message || 'Erro ao vincular registro');
    } finally {
      setLinking(false);
    }
  };

  const registrosPorTipo: { [key: string]: any[] } = {
    contact: registrosVinculados.contacts,
    nif: registrosVinculados.nifs,
    visaAnalysis: registrosVinculados.visaAnalyses,
    residenceAnalysis: registrosVinculados.residenceAnalyses,
    contrato: registrosVinculados.contratos
  };

  const recordLabel = (tipo: string, registro: any) => {
    if (tipo === 'contact') return registro.name;
    if (tipo === 'visaAnalysis' || tipo === 'residenceAnalysis') {
      return registro.clientLabel || registro.clientEmailHint || registro._id;
    }
    return `${registro.nome || ''} ${registro.sobrenome || ''}`.trim();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Cliente não encontrado.</p>
      </div>
    );
  }

  // Ações da parcela: mesma marcação na tabela (desktop) e nos cards (mobile).
  const renderPagamentoActions = (pagamento: (typeof pagamentos)[number]) => (
    <div className="row-actions md:justify-end">
      {(pagamento.status === 'pendente' || pagamento.status === 'atrasado') && (
        <ActionButton
          action="approve"
          label="Marcar pago"
          onClick={() => handleMarcarPago(pagamento._id)}
        />
      )}
      <ActionButton
        action="edit"
        onClick={() => setEditingPagamento(pagamento)}
      />
      {pagamento.status !== 'pago' && (
        <ActionButton
          action="delete"
          onClick={() => handleExcluirPagamento(pagamento._id)}
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { label: t('breadcrumbs.dashboard'), href: '/admin' },
              { label: t('admin.clientes'), href: '/admin/crm/clientes' },
              { label: cliente.nome }
            ]}
          />
        </div>

        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <IconButton action="back" onClick={() => navigate('/admin/crm/clientes')} />
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl font-bold text-gray-900">{cliente.nome}</h1>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[cliente.status]}`}>
                  {STATUS_LABELS[cliente.status]}
                </span>
              </div>
              <p className="text-gray-600">{cliente.email || 'Sem email'} · {cliente.telefone || 'Sem telefone'}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={async () => {
                // Sem venda, o contrato abre com o plano padrão; com venda, segue valor e parcelas dela.
                const padrao = { clienteId: id, nomeCliente: cliente.nome, formaPagamento: 'parcelado', numeroParcelas: 3, intervaloDias: 30 };
                let prefillContrato: Record<string, unknown> = padrao;
                try {
                  const res = await axios.get(`/clientes/${id}/prefill-contrato`);
                  const doServidor = res.data.prefillContrato || {};
                  prefillContrato = doServidor.formaPagamento ? doServidor : { ...padrao, ...doServidor };
                } catch {
                  // segue com o padrão
                }
                navigate('/admin/contrato', { state: { prefillContrato } });
              }}
            >
              Novo contrato
            </Button>
            <Button variant="secondary" icon="edit" to={`/admin/clientes/${id}/edit`}>
              Editar cadastro
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Vendas */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Vendas</h3>
                <Button size="sm" to={`/admin/vendas/new?clienteId=${id}`}>
                  <Plus className="w-4 h-4" />
                  <span>Nova venda</span>
                </Button>
              </div>
              {vendas.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhuma venda registrada.</p>
              ) : (
                <>
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                      <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <th className="pb-2 pr-4">Descrição</th>
                        <th className="pb-2 pr-4">Valor</th>
                        <th className="pb-2 pr-4">Forma</th>
                        <th className="pb-2 pr-4">Status</th>
                        <th className="pb-2">Data</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {vendas.map((venda) => (
                        <tr key={venda._id}>
                          <td className="py-2 pr-4">
                            <Link to={`/admin/vendas/${venda._id}/edit`} className="text-primary-600 hover:text-primary-800">
                              {venda.descricao}
                            </Link>
                          </td>
                          <td className="py-2 pr-4">{formatMoeda(venda.valorTotal, venda.moeda)}</td>
                          <td className="py-2 pr-4">{venda.formaPagamento}</td>
                          <td className="py-2 pr-4">{venda.status}</td>
                          <td className="py-2">{formatDate(venda.dataVenda)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ul className="md:hidden divide-y divide-gray-100">
                  {vendas.map((venda) => (
                    <li key={venda._id}>
                      <Link to={`/admin/vendas/${venda._id}/edit`} className="flex items-start justify-between gap-3 py-3 min-h-[44px]">
                        <div className="min-w-0">
                          <p className="font-medium text-primary-600 truncate">{venda.descricao}</p>
                          <p className="text-xs text-gray-500">{venda.formaPagamento} · {venda.status} · {formatDate(venda.dataVenda)}</p>
                        </div>
                        <span className="shrink-0 font-medium text-gray-900">{formatMoeda(venda.valorTotal, venda.moeda)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                </>
              )}
            </div>

            {/* Pagamentos */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pagamentos</h3>
              {pagamentos.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhuma parcela registrada.</p>
              ) : (
                <>
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                      <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <th className="pb-2 pr-4">Parcela</th>
                        <th className="pb-2 pr-4">Valor</th>
                        <th className="pb-2 pr-4">Vencimento</th>
                        <th className="pb-2 pr-4">Status</th>
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pagamentos.map((pagamento) => (
                        <tr key={pagamento._id}>
                          <td className="py-2 pr-4">{pagamento.numeroParcela}/{pagamento.totalParcelas}</td>
                          <td className="py-2 pr-4">{formatMoeda(pagamento.valor, pagamento.moeda)}</td>
                          <td className="py-2 pr-4">{formatDate(pagamento.vencimento)}</td>
                          <td className="py-2 pr-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${PAGAMENTO_STATUS_COLORS[pagamento.status]}`}>
                              {pagamento.status}
                            </span>
                          </td>
                          <td className="py-2">
                            {renderPagamentoActions(pagamento)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ul className="md:hidden divide-y divide-gray-100">
                  {pagamentos.map((pagamento) => (
                    <li key={pagamento._id} className="py-3 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-gray-900">
                            Parcela {pagamento.numeroParcela}/{pagamento.totalParcelas} · {formatMoeda(pagamento.valor, pagamento.moeda)}
                          </p>
                          <p className="text-xs text-gray-500">Vencimento: {formatDate(pagamento.vencimento)}</p>
                        </div>
                        <span className={`shrink-0 px-2 py-1 text-xs font-medium rounded-full ${PAGAMENTO_STATUS_COLORS[pagamento.status]}`}>
                          {pagamento.status}
                        </span>
                      </div>
                      {renderPagamentoActions(pagamento)}
                    </li>
                  ))}
                </ul>
                </>
              )}
            </div>

            <ClienteDocumentosSection
              clienteId={cliente._id}
              clienteNome={cliente.nome}
              clienteEmail={cliente.email}
              onChanged={refreshHistorico}
            />

            {/* Registros vinculados */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Registros vinculados</h3>
              <div className="space-y-4">
                {LINK_TYPES.map(({ tipo, label, icon: Icon }) => (
                  <div key={tipo} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                        <Icon className="w-4 h-4" />
                        <span>{label}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setLinkingTipo(linkingTipo === tipo ? null : tipo)}
                        className="text-xs text-primary-600 hover:text-primary-800 text-left sm:text-right"
                      >
                        {linkingTipo === tipo ? 'Cancelar' : 'Vincular registro existente'}
                      </button>
                    </div>

                    {registrosPorTipo[tipo].length === 0 ? (
                      <p className="text-sm text-gray-500">Nenhum registro vinculado.</p>
                    ) : (
                      <ul className="space-y-1">
                        {registrosPorTipo[tipo].map((registro: any) => (
                          <li key={registro._id} className="text-sm text-gray-700">
                            {recordLabel(tipo, registro)}
                          </li>
                        ))}
                      </ul>
                    )}

                    {linkingTipo === tipo && (
                      <div className="mt-3 space-y-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <input
                            type="text"
                            value={linkQuery}
                            onChange={(e) => setLinkQuery(e.target.value)}
                            placeholder="Buscar por nome ou email..."
                            className="field pl-9 pr-3"
                          />
                        </div>
                        {linkResults.length > 0 && (
                          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                            {linkResults.map((registro: any) => (
                              <li key={registro._id} className="flex items-center justify-between px-3 py-2 text-sm">
                                <span>{recordLabel(tipo, registro)}</span>
                                <button
                                  type="button"
                                  disabled={linking}
                                  onClick={() => handleLinkRecord(registro._id)}
                                  className="text-primary-600 hover:text-primary-800 disabled:opacity-50"
                                >
                                  Vincular
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Cadastro */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cadastro</h3>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-gray-500">Email</dt>
                  <dd className="text-gray-900">{cliente.email || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Telefone</dt>
                  <dd className="text-gray-900">{cliente.telefone || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Passaporte</dt>
                  <dd className="text-gray-900">{cliente.passaporteNumero || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">NIF</dt>
                  <dd className="text-gray-900">{cliente.nif || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">CPF</dt>
                  <dd className="text-gray-900">{cliente.cpf || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Endereço</dt>
                  <dd className="text-gray-900">
                    {cliente.endereco?.cidade || '—'}{cliente.endereco?.pais ? `, ${cliente.endereco.pais}` : ''}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Origem</dt>
                  <dd className="text-gray-900">{cliente.origem || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Responsável</dt>
                  <dd className="text-gray-900">{cliente.responsavelId?.name || '—'}</dd>
                </div>
                {cliente.observacoes && (
                  <div>
                    <dt className="text-gray-500">Observações</dt>
                    <dd className="text-gray-900 whitespace-pre-wrap">{cliente.observacoes}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Histórico */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Histórico</h3>
              <div className="flex space-x-2 mb-4">
                <input
                  type="text"
                  value={novaNota}
                  onChange={(e) => setNovaNota(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddNote())}
                  placeholder="Adicionar nota..."
                  className="field flex-1"
                />
                <Button size="sm"
                  onClick={handleAddNote}
                  disabled={addingNote || !novaNota.trim()}>
                  Adicionar
                </Button>
              </div>
              <ul className="space-y-3 max-h-96 overflow-y-auto">
                {historico.map((item) => (
                  <li key={item._id} className="text-sm border-l-2 border-gray-200 pl-3">
                    <div className="flex items-center space-x-1 text-gray-400 text-xs">
                      <Clock className="w-3 h-3" />
                      <span>{formatDateTime(item.createdAt)}</span>
                      <span>·</span>
                      <span>{item.autor?.name || 'Sistema'}</span>
                    </div>
                    <p className="text-gray-800">{item.descricao}</p>
                  </li>
                ))}
                {historico.length === 0 && (
                  <p className="text-sm text-gray-500">Nenhum evento registrado ainda.</p>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {editingPagamento && (
        <EditParcelaModal
          pagamento={editingPagamento}
          onClose={() => setEditingPagamento(null)}
          onSaved={() => {
            setEditingPagamento(null);
            fetchDetail();
          }}
        />
      )}
    </div>
  );
};

export default ClienteDetail;
